import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography
} from '@mui/material';
import { Edit2, Trash2, Plus } from 'lucide-react';
import { WorkstreamType } from './types';
import { CleanupButton } from './CleanupButton';

interface WorkstreamTypeManagerProps {
  open: boolean;
  onClose: () => void;
  workstreamTypes: WorkstreamType[];
  onAdd: (type: Omit<WorkstreamType, 'id'>) => Promise<void>;
  onUpdate: (id: string, type: Partial<WorkstreamType>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const DEFAULT_TYPE_NAMES = ['FEATURE', 'BUGFIX', 'RESEARCH', 'DESIGN', 'TESTING', 'DEPLOYMENT'];

export const WorkstreamTypeManager: React.FC<WorkstreamTypeManagerProps> = ({
  open,
  onClose,
  workstreamTypes,
  onAdd,
  onUpdate,
  onDelete
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<WorkstreamType | null>(null);
  const [formData, setFormData] = useState({
    label: '',
    color: '#2196F3'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate the internal name from the display label
  const generatedName = useMemo(() => {
    return formData.label
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '')
      .replace(/\s+/g, '_');
  }, [formData.label]);

  const handleOpenDialog = (type?: WorkstreamType) => {
    if (type) {
      setEditingType(type);
      setFormData({
        label: type.label,
        color: type.color
      });
    } else {
      setEditingType(null);
      setFormData({
        label: '',
        color: '#2196F3'
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.label.trim()) return;

    setIsSubmitting(true);
    try {
      const typeData = {
        name: generatedName,
        label: formData.label,
        color: formData.color
      };

      if (editingType) {
        await onUpdate(editingType.id, typeData);
      } else {
        await onAdd(typeData);
      }
      setIsDialogOpen(false);
      setFormData({
        label: '',
        color: '#2196F3'
      });
    } catch (error) {
      console.error('Error saving workstream type:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDefaultType = (type: WorkstreamType): boolean => {
    return DEFAULT_TYPE_NAMES.includes(type.name);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Workstream Types</DialogTitle>
      <DialogContent>
        {/* Add the CleanupButton and adjust layout */}
        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: 2
        }}>
          <Button
            startIcon={<Plus size={16} />}
            onClick={() => handleOpenDialog()}
            variant="outlined"
            size="small"
          >
            Add Type
          </Button>
          <CleanupButton />
        </Box>

        <List>
          {workstreamTypes.map((type) => (
            <ListItem key={type.id}>
              <ListItemText
                primary={type.label}
                secondary={`Type: ${type.name}`}
              />
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: type.color,
                  mr: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleOpenDialog(type)}
                  sx={{ mr: 1 }}
                >
                  <Edit2 size={16} />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => onDelete(type.id)}
                  disabled={isDefaultType(type)}
                  sx={{
                    '&.Mui-disabled': {
                      opacity: 0.3
                    }
                  }}
                >
                  <Trash2 size={16} />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        <Dialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingType ? 'Edit Workstream Type' : 'Add Workstream Type'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Display Label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                helperText="Enter a descriptive label for this workstream type"
              />
              
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="caption" color="text.secondary">
                  Internal Identifier
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace',
                    mt: 0.5
                  }}
                >
                  {generatedName || '(enter a display label)'}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting || !formData.label.trim()}
            >
              {isSubmitting
                ? 'Saving...'
                : editingType
                ? 'Save Changes'
                : 'Add Type'}
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};